/**
 * The zone WAND — a click that adds to a zone (➕) or takes from it (➖), the
 * correction ADR-031 §3.2 promised as T3. Allen's design (2026-09-25):
 * "click it — expand the fill to include the faces immediately surrounding the
 * click, with attraction to the faces already filled"; and the minus "the same
 * but inverse — remove faces likely assigned to body parts that are not wanted".
 *
 * ONE SHAPE FOR BOTH: an ellipse over the surface whose foci are the click and
 * what the stroke is ATTRACTED to — the zone for ➕, the body side of the zone's
 * edge for ➖. A face is taken when its path click → face → attractor is no more
 * than the click's own distance to the attractor plus the brush radius: the
 * bridge between click and zone fills, and the patch bulges toward the glow
 * rather than spreading evenly.
 *
 * THE CUE is distance from the body's skeleton (the unarmatured detector's
 * measurement — Tripo paints a tail to the pelvis and thighs, so bone ownership
 * cannot tell tail from hip; Kitty, measured 2026-09-25). A face near the
 * body's bones is costly to cross for ➕ and cheap for ➖; flesh no bone reaches
 * the other way round. Texture colour is the planned second cue.
 *
 * After a ➖, only the pieces holding flesh past the detector's threshold are
 * kept: a subtraction never leaves an island of body that would take a bone.
 *
 * Kernel-pure; faces are triangles of one mesh (the Zone's own indices).
 * Depends on: nothing. Depended on by: the app's zone wand door.
 */

export interface WandMesh {
  /** Bind-world positions, xyz interleaved. */
  readonly positions: Float32Array;
  readonly indices: Uint32Array | Uint16Array;
  /** Per VERTEX: distance to the nearest BODY bone segment (no grafted bones) — `nearestSegmentDistances`. */
  readonly armature: Float32Array;
}

export interface WandStroke {
  readonly mode: 'add' | 'remove';
  /** The clicked triangle. */
  readonly face: number;
  /** Brush radius, world units, along the surface. */
  readonly radius: number;
  /**
   * ➖ only: drop pieces of the zone left holding no flesh past the bones (default true — a tail's
   * rule: a piece of body alone would take a bone). FALSE for a zone that LIVES on the bones —
   * attached hair hugs the skull, every face of it near Head, so the rule dropped the whole zone on
   * the first ➖ ("that would remove all of the hair", Allen 2026-10-05).
   */
  readonly dropBodyIslands?: boolean;
}

export interface WandResult {
  /** The zone's faces after the stroke, ascending. */
  readonly faces: number[];
  /** Faces the stroke added (➕) or removed (➖), including islands a ➖ dropped. */
  readonly changed: number;
  /** ➖ only: body islands the stroke left and the wand dropped with it. */
  readonly islandsDropped: number;
}

/** How much dearer a face of the wrong kind is to cross (body for ➕, tail for ➖). */
const WAND_CUE_GAIN = 3;

class WandRefusal extends Error {}
export { WandRefusal };

/** Apply one stroke to a zone. Pure. */
export function wandStroke(mesh: WandMesh, zone: readonly number[], stroke: WandStroke): WandResult {
  const faceCount = Math.floor(mesh.indices.length / 3);
  if (!Number.isInteger(stroke.face) || stroke.face < 0 || stroke.face >= faceCount) {
    throw new WandRefusal(`the wand's face ${stroke.face} is not on this mesh (${faceCount} faces)`);
  }
  if (!(stroke.radius > 0)) throw new WandRefusal(`the wand's radius ${stroke.radius} is not positive`);
  const inZone = new Uint8Array(faceCount);
  for (const f of zone) inZone[f] = 1;

  // ---- per face: centroid, and how BODY-like it is (0 = no bone reaches, 1 = on the bones) ----
  const cx = new Float64Array(faceCount);
  const cy = new Float64Array(faceCount);
  const cz = new Float64Array(faceCount);
  const arm = new Float64Array(faceCount);
  for (let f = 0; f < faceCount; f++) {
    let a = 0;
    for (let k = 0; k < 3; k++) {
      const v = mesh.indices[f * 3 + k]!;
      cx[f]! += mesh.positions[v * 3]! / 3;
      cy[f]! += mesh.positions[v * 3 + 1]! / 3;
      cz[f]! += mesh.positions[v * 3 + 2]! / 3;
      a += mesh.armature[v]! / 3;
    }
    arm[f] = a;
  }
  const sortedArm = Float64Array.from(mesh.armature).sort();
  const median = sortedArm[Math.floor(sortedArm.length / 2)]!;
  // The unarmatured detector's threshold: flesh past 2 medians is flesh no bone reaches.
  const threshold = 2 * median;
  const bodyness = (f: number): number => Math.min(1, Math.max(0, (threshold - arm[f]!) / Math.max(threshold - median, 1e-9)));

  // ---- face adjacency through welded vertices (a UV seam never cuts the surface) ----
  const weld = new Map<string, number>();
  const rep = new Int32Array(mesh.positions.length / 3);
  for (let v = 0; v < rep.length; v++) {
    const k = `${mesh.positions[v * 3]},${mesh.positions[v * 3 + 1]},${mesh.positions[v * 3 + 2]}`;
    let r = weld.get(k);
    if (r === undefined) {
      r = v;
      weld.set(k, v);
    }
    rep[v] = r;
  }
  const facesOf = new Map<number, number[]>();
  for (let f = 0; f < faceCount; f++) {
    for (let k = 0; k < 3; k++) {
      const r = rep[mesh.indices[f * 3 + k]!]!;
      let list = facesOf.get(r);
      if (list === undefined) {
        list = [];
        facesOf.set(r, list);
      }
      list.push(f);
    }
  }
  const neighbours = (f: number): number[] => {
    const out = new Set<number>();
    for (let k = 0; k < 3; k++) for (const g of facesOf.get(rep[mesh.indices[f * 3 + k]!]!)!) if (g !== f) out.add(g);
    return [...out];
  };

  const wrongKind = (f: number): number => (stroke.mode === 'add' ? bodyness(f) : 1 - bodyness(f));
  const step = (f: number, g: number): number =>
    Math.hypot(cx[g]! - cx[f]!, cy[g]! - cy[f]!, cz[g]! - cz[f]!) * (1 + WAND_CUE_GAIN * (wrongKind(f) + wrongKind(g)) * 0.5);
  /** Weighted surface distance from `sources`, stopping past `limit`. */
  const reach = (sources: readonly number[], limit: number): Float64Array => {
    const dist = new Float64Array(faceCount).fill(Infinity);
    const heap: [number, number][] = [];
    const push = (d: number, f: number): void => {
      heap.push([d, f]);
      let i = heap.length - 1;
      while (i > 0) {
        const p = (i - 1) >> 1;
        if (heap[p]![0] <= heap[i]![0]) break;
        [heap[p], heap[i]] = [heap[i]!, heap[p]!];
        i = p;
      }
    };
    const pop = (): [number, number] => {
      const top = heap[0]!;
      const last = heap.pop()!;
      if (heap.length > 0) {
        heap[0] = last;
        let i = 0;
        for (;;) {
          const l = 2 * i + 1;
          const r = l + 1;
          let m = i;
          if (l < heap.length && heap[l]![0] < heap[m]![0]) m = l;
          if (r < heap.length && heap[r]![0] < heap[m]![0]) m = r;
          if (m === i) break;
          [heap[m], heap[i]] = [heap[i]!, heap[m]!];
          i = m;
        }
      }
      return top;
    };
    for (const s of sources) {
      dist[s] = 0;
      push(0, s);
    }
    while (heap.length > 0) {
      const [d, f] = pop();
      if (d > dist[f]! || d > limit) continue;
      for (const g of neighbours(f)) {
        const nd = d + step(f, g);
        if (nd < dist[g]!) {
          dist[g] = nd;
          push(nd, g);
        }
      }
    }
    return dist;
  };

  // The ATTRACTOR: the zone (➕), or the body side of its edge (➖) — faces outside it touching it.
  let attractor: number[];
  if (stroke.mode === 'add') {
    attractor = [...zone];
  } else {
    const edge = new Set<number>();
    for (const f of zone) for (const g of neighbours(f)) if (inZone[g] === 0) edge.add(g);
    attractor = [...edge];
  }
  if (attractor.length === 0) {
    // ➕ on an empty zone, or ➖ on a zone that is the whole mesh: the click alone decides.
    attractor = [stroke.face];
  }
  const fromAttractor = reach(attractor, Infinity);
  const gap = fromAttractor[stroke.face]!;
  // A NEW zone (nothing to be attracted to): the click is both foci, so the
  // ellipse is a disc — give it the brush's full reach, not half of it (the bear
  // girl's bob, 2026-09-25: a half-reach first click took 2.9 cm of a ~9 cm puff).
  const budget = zone.length === 0 ? 2 * stroke.radius : (Number.isFinite(gap) ? gap : 0) + stroke.radius;
  const fromClick = reach([stroke.face], budget);

  // THE GATE (measured on Kitty: a ➕ between two tails bridged 71 faces of hip
  // surface): ➕ never takes flesh ON the bones (within the median distance),
  // ➖ never takes flesh no bone reaches (past the threshold) — unless the person
  // clicked on exactly that kind of flesh, which is their answer.
  const clicked = arm[stroke.face]!;
  const gated = (f: number): boolean =>
    stroke.mode === 'add' ? arm[f]! <= median && clicked > median : arm[f]! > threshold && clicked <= threshold;
  const taken: number[] = [];
  for (let f = 0; f < faceCount; f++) {
    const want = stroke.mode === 'add' ? inZone[f] === 0 : inZone[f] === 1;
    if (want && !gated(f) && fromClick[f]! + fromAttractor[f]! <= budget) taken.push(f);
  }
  // The clicked face itself always answers the click.
  if ((stroke.mode === 'add' ? inZone[stroke.face] === 0 : inZone[stroke.face] === 1) && !taken.includes(stroke.face)) taken.push(stroke.face);

  const next = new Uint8Array(inZone);
  for (const f of taken) next[f] = stroke.mode === 'add' ? 1 : 0;
  let islandsDropped = 0;
  let dropped = 0;
  if (stroke.mode === 'remove' && stroke.dropBodyIslands !== false) {
    // Keep only the pieces holding flesh no bone reaches; a piece of body alone would take a bone.
    const seen = new Uint8Array(faceCount);
    for (let f = 0; f < faceCount; f++) {
      if (next[f] === 0 || seen[f] === 1) continue;
      const piece: number[] = [];
      const queue = [f];
      seen[f] = 1;
      while (queue.length > 0) {
        const u = queue.pop()!;
        piece.push(u);
        for (const g of neighbours(u)) {
          if (next[g] === 1 && seen[g] === 0) {
            seen[g] = 1;
            queue.push(g);
          }
        }
      }
      if (!piece.some((u) => arm[u]! > threshold)) {
        for (const u of piece) next[u] = 0;
        islandsDropped++;
        dropped += piece.length;
      }
    }
  }
  const faces: number[] = [];
  for (let f = 0; f < faceCount; f++) if (next[f] === 1) faces.push(f);
  return { faces, changed: taken.length + dropped, islandsDropped };
}
